import React, { useState, useMemo, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Card, Typography, Button, Input } from "@material-tailwind/react";
import Layout from "../components/layout/Layout";
import { colors } from "../utils/colors";
import {
  MagnifyingGlassIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ExclamationTriangleIcon,
  PlusIcon
} from "@heroicons/react/24/outline";
import { useProperties } from "../contexts/PropertiesContext";
import RecommendedDeals from "../components/deals/RecommendedDeals";
import { trackEvent } from "../utils/analytics";

const MAX_INITIAL_ITEMS = 3;
const ITEMS_PER_LOAD = 10;

// Helper function to get a user-friendly status and color
const getStatusDisplay = (statusKey, type) => {
  let statusDetails = { text: statusKey, color: "text-gray-600", bgColor: "bg-gray-50" }; // Default

  if (type === 'quotes') {
    switch(statusKey) {
      case 'waiting_response':
        statusDetails = { text: "En attente de validation", color: "text-yellow-600", bgColor: "bg-yellow-50" }; // Corresponds to QuoteSelector label
        break;
      case 'accepted':
        statusDetails = { text: "Devis validé", color: "text-green-600", bgColor: "bg-green-50" }; // Corresponds to QuoteSelector label
        break;
      case 'rejected':
        statusDetails = { text: "Devis refusé", color: "text-red-600", bgColor: "bg-red-50" }; // Corresponds to QuoteSelector label
        break;
      case 'waiting_quote': // Not explicitly in QuoteSelector, but keeping for completeness
      default: // if statusKey is 'waiting_quote' or any other not listed for quotes
        statusDetails = { text: "Devis généré", color: "text-blue-600", bgColor: "bg-blue-50" }; // Default for quotes as in QuoteSelector
        break;
    }
  } else if (type === 'properties') {
    switch (statusKey) {
      case 'appointment_confirmed':
        statusDetails = { text: "RDV confirmé", color: "text-cyan-600", bgColor: "bg-cyan-50" }; // Matches PropertySelector: cyan
        break;
      case 'appointment_scheduled':
        statusDetails = { text: "RDV programmé", color: "text-sky-600", bgColor: "bg-sky-50" }; // Matches PropertySelector: light-blue (sky)
        break;
      case 'invoice_pending':
        statusDetails = { text: "Facture en attente", color: "text-amber-600", bgColor: "bg-amber-50" }; // Matches PropertySelector: amber
        break;
      case 'payment_required':
        statusDetails = { text: "Paiement requis", color: "text-red-600", bgColor: "bg-red-50" }; // Matches PropertySelector: red
        break;
      case 'dpe_consent_required':
        statusDetails = { text: "Accord DPE requis", color: "text-orange-600", bgColor: "bg-orange-50" }; // Matches PropertySelector: orange
        break;
      case 'documents_required':
        statusDetails = { text: "Documents requis", color: "text-orange-600", bgColor: "bg-orange-50" }; // Matches PropertySelector: orange
        break;
      case 'report_in_preparation':
        statusDetails = { text: "Rapport en préparation", color: "text-slate-600", bgColor: "bg-slate-50" }; // Matches PropertySelector: blue-gray (slate)
        break;
      case 'report_ready':
        statusDetails = { text: "Rapport prêt", color: "text-green-600", bgColor: "bg-green-50" }; // Matches PropertySelector: green
        break;
      default:
        statusDetails = { text: "Statut inconnu", color: "text-gray-600", bgColor: "bg-gray-50" }; // Default for properties
        break;
    }
  }
  return statusDetails;
};

const DataSection = ({ title, items, type, emptyMessage, getLink, getStatusKey, getName, getId, getAddress }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [visibleCount, setVisibleCount] = useState(MAX_INITIAL_ITEMS);
  const navigate = useNavigate();

  const filteredItems = useMemo(() => {
    if (!items || items.length === 0) return [];
    return items.filter(item => {
      const name = getName(item) || "";
      const id = getId(item) ? getId(item).toString() : "";
      const status = getStatusKey(item) || "";
      const searchLower = searchTerm.toLowerCase();
      const nameAndId = `${name} - N° ${id}`.toLowerCase();
      return nameAndId.includes(searchLower) ||
             status.toLowerCase().includes(searchLower) ||
             getAddress(item).toLowerCase().includes(searchLower);
    });
  }, [items, searchTerm, getName, getId, getStatusKey, getAddress]);

  const itemsToShow = filteredItems.slice(0, visibleCount);

  const handleShowMore = () => {
    setVisibleCount(prevCount => prevCount + ITEMS_PER_LOAD);
  };
  
  const handleShowLess = () => {
    setVisibleCount(MAX_INITIAL_ITEMS);
  };

  const handleConsulterClick = (item) => {
    const path = getLink(item);
    trackEvent(`dashboard_click_consulter_${type}`, { itemId: getId(item) });
    navigate(path);
  };
  
  if (!items || items.length === 0) {
    return null;
  }

  return (
    <div className="mb-8">
      <Typography variant="h4" className="mb-4 text-black">{title}</Typography>
      {items.length > 10 && (
        <div className="mb-4">
          <Input
            type="text"
            label={`Rechercher par ${type === "quotes" ? "devis" : "dossier"}, ID, adresse ou statut...`}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            icon={<MagnifyingGlassIcon className="h-5 w-5" />}
            containerProps={{ className: "min-w-[100px]" }}
         variant="outlined"
/>
        </div>
      )}
      <div className="space-y-3">
        {itemsToShow.map((item) => {
          const statusDisplay = getStatusDisplay(getStatusKey(item), type);
    return (
            <Card key={getId(item)} className="p-4 border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center">
                <div className="mb-2 sm:mb-0 flex-grow">
                  <Typography variant="h6" color="blue-gray" className="capitalize truncate">
                   {getName(item)} - N° {getId(item)}
                  </Typography>
                   <Typography variant="small" color="gray" className="mt-1 truncate">
                      Adresse : {getAddress(item) || "N/A"}
                    </Typography>
            </div>
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 mt-2 sm:mt-0 sm:ml-4 flex-shrink-0">
                   <span className={`px-2 py-1 text-xs font-medium rounded-full whitespace-nowrap ${statusDisplay.bgColor} ${statusDisplay.color}`}>
                     {statusDisplay.text} 
                   </span>
                  {/* Display actionsNeeded for properties before "Consulter" button */}
                  {type === "properties" && propertyHasActions(item) && (
                    <Link to={`/actions/${getId(item)}`} className="block">
                      <Button
                        size="sm"
                        variant="outlined"
                        style={{ borderColor: colors.dimoPrimary, color: colors.dimoPrimary }}
                        className="normal-case whitespace-nowrap"
                        onClick={() => trackEvent('dashboard_click_actions_needed', { propertyId: getId(item) })}
                      >
                        Compléter mon dossier
                      </Button>
                    </Link>
                  )}
                  <Button
                    size="sm"
                    variant="filled"
                    style={{ backgroundColor: colors.dimoPrimary }}
                    className="normal-case whitespace-nowrap"
                    onClick={() => handleConsulterClick(item)}
                  >
                    Consulter
                  </Button>
                  </div>
            </div>
            </Card>
          );
        })}
                  </div>
      {filteredItems.length > visibleCount && (
        <Button
          variant="text"
          color="blue-gray"
          onClick={handleShowMore}
          className="mt-4 flex items-center gap-2 normal-case mx-auto"
        >
          Voir plus <ChevronDownIcon className="h-4 w-4"/>
        </Button>
      )}
      {visibleCount > MAX_INITIAL_ITEMS && filteredItems.length <= visibleCount && (
         <Button
          variant="text"
          color="blue-gray"
          onClick={handleShowLess}
          className="mt-4 flex items-center gap-2 normal-case mx-auto"
        >
          Voir moins <ChevronUpIcon className="h-4 w-4"/>
        </Button>
      )}
       {filteredItems.length === 0 && searchTerm && (
        <Typography color="gray" className="text-center mt-4">
          Aucun résultat trouvé pour "{searchTerm}".
        </Typography>
      )}
                      </div>
  );
};

// Helper to check for actions needed (specific to properties)
const propertyHasActions = (property) => {
  return property && property.actionsNeeded && Object.values(property.actionsNeeded).some(needed => needed === true);
};

export default function Dashboard() {
  const { properties, quotes, loading, error } = useProperties();
  const [isOrganization, setIsOrganization] = useState(false);

  useEffect(() => {
    const token = document.cookie.split('; ').find(row => row.startsWith('authToken='));
    if (token) {
      const tokenValue = token.split('=')[1];
      try {
        const decodedToken = JSON.parse(atob(tokenValue.split('.')[1]));
        setIsOrganization(decodedToken.is_organization || false); // Assurer que c'est un booléen
      } catch (error) {
        console.error("Erreur lors du décodage du token:", error);
        setIsOrganization(false);
      }
    }
  }, []);

  const newQuoteLink = isOrganization 
    ? "https://app.dimo-diagnostic.net/mon-offre?s=1&o=devis-pro"
    : "https://www.dimo-diagnostic.net/devis-diagnostic-immobilier-tarifs";
  
  // Skeleton loader structure
  const SkeletonLoader = () => (
    <div className="space-y-8">
      {[...Array(2)].map((_, sectionIndex) => ( // For two sections: Quotes and Properties
        <div key={sectionIndex}>
          <div className="h-8 bg-gray-200 rounded w-1/3 mb-4"></div> {/* Title skeleton */}
          <div className="h-10 bg-gray-200 rounded w-full mb-4"></div> {/* Search bar skeleton */}
          <div className="space-y-3">
            {[...Array(MAX_INITIAL_ITEMS)].map((_, itemIndex) => (
              <Card key={itemIndex} className="p-4 border border-gray-200 shadow-sm">
                <div className="flex justify-between items-center">
                  <div className="flex-grow">
                    <div className="h-6 bg-gray-200 rounded w-3/4 mb-1"></div>
                    <div className="h-4 bg-gray-200 rounded w-5/6"></div> {/* Address skeleton */}
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0 ml-4">
                    <div className="h-6 bg-gray-200 rounded-full w-24"></div>
                    <div className="h-8 bg-gray-200 rounded w-24"></div>
                  </div>
                </div>
              </Card>
            ))}
            </div>
          <div className="h-8 bg-gray-200 rounded w-28 mt-4 mx-auto"></div> {/* "Voir plus" button skeleton */}
                      </div>
                    ))}
                  </div>
  );

  if (loading) {
    return (
      <Layout>
        <Card className="mb-6 shadow-md">
          <div className="p-6 space-y-6">
             <div className="">
              <div className="h-8 bg-gray-200 rounded w-48 mb-2"></div>
              <div className="h-5 bg-gray-200 rounded w-64"></div>
            </div>
            <SkeletonLoader />
          </div>
        </Card>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <Card className="p-6 text-center">
        <Typography color="red">{error}</Typography>
        </Card>
      </Layout>
    );
  }

  const noQuotes = !quotes || quotes.length === 0;
  const noProperties = !properties || properties.length === 0;

  return (
    <Layout>
      <Card className="mb-6 shadow-md">
        <div className="p-6 space-y-6">
          <div className="">
            <Typography variant="h3" className="mb-2 text-black">Bonjour 👋</Typography>
            <Typography variant="paragraph" className="mb-2 text-black">Bienvenue sur votre espace client !</Typography>
          </div>

          {noQuotes && noProperties && (
            <Card className="p-6 bg-gray-50 border border-gray-200 shadow-md text-center">
              <Typography variant="h5" color="blue-gray" className="mb-4 flex items-center justify-center">
                <ExclamationTriangleIcon className="h-6 w-6 mr-2" style={{ color: colors.dimoPrimary }} />
                Vous n'avez aucun devis ou dossier en cours.
              </Typography>
              <a href={newQuoteLink} target="_blank" rel="noopener noreferrer">
                <Button
                  style={{ backgroundColor: colors.dimoPrimary }}
                  className="flex items-center justify-center gap-3 mx-auto normal-case"
                >
                  <PlusIcon className="h-5 w-5" />
                  Demander un nouveau devis
                </Button>
              </a>
                  </Card>
          )}

          {(!noQuotes || !noProperties) && (
            <>
              <DataSection
                title="Vos devis en cours"
                items={quotes}
                type="quotes"
                emptyMessage="Vous n'avez pas de devis en cours."
                getLink={(item) => `/quote/${item.id}`}
                getStatusKey={(item) => item.quoteStatus}
                getName={(item) => item.name}
                getId={(item) => item.id}
                getAddress={(item) => item.address}
              />

              <DataSection
                title={properties && properties.length > 1 ? "Vos dossiers" : "Votre dossier"}
                items={properties}
                type="properties"
                emptyMessage="Vous n'avez pas de dossier en cours."
                getLink={(item) => `/diagnostics/${item.id}`}
                getStatusKey={(item) => item.propertyStatus}
                getName={(item) => item.name}
                getId={(item) => item.id}
                getAddress={(item) => item.address}
              />
            </>
          )}

        </div>
      </Card>
      
      {/* Section Bons Plans */}
      <div className="mb-6">
        <RecommendedDeals source="dashboard" />
      </div>
    </Layout>
  );
}